import type { SteppedProgressState } from "@imminently/decisively-ui/SteppedProgress";
import type { Release, ReleaseAdapterState, ReleaseAdapterOwnedResources } from "@packages/commons";

import { getAdapterPersistenceId, omitAnswers, omitSecretAnswers, type AdapterApplyAnswers, type AdapterDefinition } from "@/features/adapters/model";
import { ADAPTER_SETUP_PROGRESS_KEY } from "@/features/adapters/model/setupProgress";


export interface AdapterWorkflowProgress {
  status: "in_progress" | "complete";
  step: number;
  stepKey: string;
  stepStatus: AdapterWorkflowStepStatus[];
  confirmations: Record<string, boolean>;
  manualStepCompletions?: Record<string, boolean>;
  /** Steps the user has been on, for steps that open once another is visited. Visiting doesn't change a step's state. */
  visitedSteps?: string[];
  connectionTests?: Record<string, { connectionId: string; testedAt: string }>;
  updatedAt: string;
}

export interface AdapterWorkflowStepStatus {
  id: string;
  state: SteppedProgressState;
}

const VISITED_STATES = new Set<string>(["in-progress", "success", "warning", "destructive"]);

/**
 * Whether a saved step state means the step was visited. Only today's state names count: anything else (such as a
 * state saved under an older name) reads as not started, like it's shown.
 */
export const isVisitedStepState = (state: string | undefined): boolean => Boolean(state && VISITED_STATES.has(state));

/**
 * The steps saved progress has visited. Progress saved before visits were tracked on their own recorded a visit as a
 * started state, so those count instead.
 */
export const getAdapterVisitedSteps = (progress: Pick<AdapterWorkflowProgress, "stepStatus" | "visitedSteps"> | undefined): string[] =>
  progress?.visitedSteps ??
  (progress?.stepStatus ?? []).filter((step) => isVisitedStepState(step.state)).map((step) => step.id);

export const addVisitedStep = (visitedSteps: string[], stepKey: string): string[] =>
  visitedSteps.includes(stepKey) ? visitedSteps : [...visitedSteps, stepKey];

const STEP_STATES = new Set<string>([...VISITED_STATES, "not-started"]);

/**
 * A step's saved state. A state saved under an older name reads as unset (so not started): renderers look states up
 * by name and would get nothing for it.
 */
export const getAdapterWorkflowStepState = (
  stepStatus: AdapterWorkflowStepStatus[] | undefined,
  stepId: string,
): AdapterWorkflowStepStatus["state"] | undefined => {
  const state = stepStatus?.find((step) => step.id === stepId)?.state;
  return state && STEP_STATES.has(state) ? state : undefined;
};

export const setAdapterWorkflowStepState = (
  stepStatus: AdapterWorkflowStepStatus[],
  stepId: string,
  state: AdapterWorkflowStepStatus["state"],
): AdapterWorkflowStepStatus[] => [
  ...stepStatus.filter((step) => step.id !== stepId),
  { id: stepId, state },
];

/** Answers as they are saved: without the in-flight setup progress or any secret. */
export const sanitizeAdapterAnswers = (definition: AdapterDefinition, answers: AdapterApplyAnswers): Record<string, unknown> =>
  omitSecretAnswers(definition, omitAnswers(answers, [ADAPTER_SETUP_PROGRESS_KEY]));

export const createAdapterWorkflow = (now = new Date().toISOString()): AdapterWorkflowProgress => ({
  status: "in_progress",
  step: 0,
  stepKey: "introduction",
  stepStatus: [],
  confirmations: {},
  manualStepCompletions: {},
  visitedSteps: [],
  updatedAt: now,
});

export const isAdapterConnectionTested = (
  workflow: Pick<AdapterWorkflowProgress, "connectionTests"> | undefined,
  connectionId: string | undefined,
): boolean => Boolean(connectionId && workflow?.connectionTests?.[connectionId]?.connectionId === connectionId);

export const setReleaseAdapterConnectionTested = (
  release: Pick<Release, "adapters">,
  definition: AdapterDefinition,
  answers: AdapterApplyAnswers,
  connectionId: string,
  testedAt: string,
): ReleaseAdapterState[] => {
  const workflow = getReleaseAdapterWorkflow(release, definition.id) ?? createAdapterWorkflow(testedAt);
  return setReleaseAdapterWorkflow(
    release,
    definition,
    {
      ...workflow,
      connectionTests: { ...workflow.connectionTests, [connectionId]: { connectionId, testedAt } },
      updatedAt: testedAt,
    },
    answers,
  );
};

export const getReleaseAdapterWorkflow = (
  release: Pick<Release, "adapters">,
  definitionId: string,
): AdapterWorkflowProgress | undefined => {
  const adapter = release.adapters?.find((candidate) => candidate.format === 2 && candidate.id === getAdapterPersistenceId(definitionId));
  const progress = adapter?.answers?.[ADAPTER_SETUP_PROGRESS_KEY];
  return progress && typeof progress === "object" ? (progress as AdapterWorkflowProgress) : undefined;
};

export const setReleaseAdapterWorkflow = (
  release: Pick<Release, "adapters">,
  definition: AdapterDefinition,
  workflow: AdapterWorkflowProgress,
  answers: AdapterApplyAnswers,
): ReleaseAdapterState[] => {
  const existing = release.adapters ?? [];
  const persistenceId = getAdapterPersistenceId(definition.id);
  const existingRecord = existing.find((adapter) => adapter.format === 2 && adapter.id === persistenceId);
  const nextRecord: ReleaseAdapterState = {
    ...existingRecord,
    format: 2,
    id: persistenceId,
    version: definition.version,
    enabled: true,
    answers: {
      ...sanitizeAdapterAnswers(definition, (existingRecord?.answers ?? {}) as AdapterApplyAnswers),
      ...sanitizeAdapterAnswers(definition, answers),
      [ADAPTER_SETUP_PROGRESS_KEY]: workflow,
    },
  };

  return existingRecord
    ? existing.map((adapter) => (adapter.format === 2 && adapter.id === persistenceId ? nextRecord : adapter))
    : [...existing, nextRecord];
};

export const setReleaseAdapterEnabled = (
  release: Pick<Release, "adapters">,
  definitionId: string,
  enabled: boolean,
): ReleaseAdapterState[] => {
  const adapters = release.adapters ?? [];
  const persistenceId = getAdapterPersistenceId(definitionId);
  if (enabled) {
    return adapters.map((adapter) => (adapter.format === 2 && adapter.id === persistenceId ? { ...adapter, enabled: true } : adapter));
  }

  const hasOwnedResources = (ownedResources: ReleaseAdapterOwnedResources | undefined): boolean =>
    Object.keys(ownedResources?.resources ?? {}).length > 0;

  return adapters.flatMap((adapter) => {
    if (adapter.format !== 2 || adapter.id !== persistenceId) return [adapter];
    if (!hasOwnedResources(adapter.ownedResources)) return [];

    return [
      {
        format: 2,
        id: adapter.id,
        version: adapter.version,
        enabled: false,
        answers: {},
        ownedResources: adapter.ownedResources,
      },
    ];
  });
};
